<template>
  <div class="p-6" v-if="probe">
    <h1 class="text-2xl font-semibold mb-6 flex items-center dark:text-white">
      <button
        @click="goBack"
        class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4 mr-1"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clip-rule="evenodd"
          />
        </svg>
        Back to Probes
      </button>
      Probe Details: {{ probe.name }}
      <div class="relative group">
        <button
          type="button"
          class="ml-2 hover:bg-transparent bg-transparent"
          @click="startTutorial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
        <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-1.5 py-0.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
      </div>
    </h1>
    <!-- Simple Tab Navigation -->
    <div class="glass-card">
      <nav class="flex px-2 py-2">
        <button
          @click="activeTab = 'probeDetails'"
          :class="[
            'ml-2 group inline-flex items-center px-4 py-2 border-t border-l border-r rounded-t-md text-sm font-medium  transition-colors duration-150 ease-in-out',
            activeTab === 'probeDetails'
              ? 'bg-white text-blue-600 border-gray-300 -mb-px dark:bg-gray-800 dark:text-blue-400'
            : 'bg-gray-100 text-gray-500 border-transparent hover:bg-gray-50 hover:text-blue-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-blue-400'
          ]"
        >
          Probe Details
        </button>
        <button
          @click="activeTab = 'snmp'"
          :class="[
            'ml-2 group inline-flex items-center px-4 py-2 border-t border-l border-r rounded-t-md text-sm font-medium  transition-colors duration-150 ease-in-out',
            activeTab === 'snmp'
              ? 'bg-white text-blue-600 border-gray-300 -mb-px dark:bg-gray-800 dark:text-blue-400'
               : 'bg-gray-100 text-gray-500 border-transparent hover:bg-gray-50 hover:text-blue-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-blue-400'
          ]"
        >
          SNMP Configurations
        </button>
        <button
          v-if="canViewProbeWifi || user()?.is_staff"
          @click="activeTab = 'wifi'"
          :class="[
            'ml-2 group inline-flex items-center px-4 py-2 border-t border-l border-r rounded-t-md text-sm font-medium  dark:text-white dark:bg-gray-700 transition-colors duration-150 ease-in-out',
            activeTab === 'wifi'
              ? 'bg-white text-blue-600 border-gray-300 -mb-px'
               : 'bg-gray-100 text-gray-500 border-transparent hover:bg-gray-50 hover:text-blue-600'
          ]"
        >
          WiFi Profiles
        </button>
      </nav>
    </div>

    <div v-show="activeTab === 'probeDetails'" class="mt-2 overflow-hidden rounded-xl backdrop-blur-md bg-white/80 dark:bg-slate-900/60" >
      <!-- Table Header -->
      <table class="w-full text-left">
      <thead class="text-white bg-gradient-to-r from-slate-900/95 to-slate-800/95 dark:from-slate-900/95 dark:to-slate-800/95 backdrop-blur-md border-b border-slate-700/50 dark:border-slate-700/50">
        <tr>
          <th class="py-2 px-4">Field</th>
          <th class="py-2 px-4">Original Value</th>
          <th class="py-2 px-4">New Value</th>
          <th class="py-2 px-4">Pending Value</th>
        </tr>
      </thead>
      <tbody class="divide-y dark:divide-gray-600/60">
        <tr
          v-for="(field, index) in displayFields"
          :key="field.key"
          :id="'probe-field-' + field.key"
          :class="['border-b dark:border-gray-700', index % 2 === 0 ? 'hover:bg-gray-900/30 transition-colors' : 'hover:bg-gray-900/30 transition-colors']"
        >
          <td class="py-4 px-4">{{ field.label }}</td>
          <td class="py-4 px-4">
            {{ formatValue(field.key, probe[field.key]) }}
          </td>
          <td class="py-4 px-4">
            <div class="flex items-center">
              <template v-if="editingField === field.key">
                <div class="flex items-start gap-2 w-full">
                  <template v-if="field.key === 'vlans'" class="relative">
                    <form @submit.prevent="saveEdit(field.key)">
                      <div class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40">
                        <div class="p-6 glass-card w-[80%] m-auto shadow-xl z-50">
                          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- NAME -->
                            <div class="flex flex-col">
                              <label for="description" class="mb-1 font-medium text-gray-700 dark:text-white">Name<span class="text-red-500">*</span>:</label>
                              <input 
                                v-model="newVLAN.name"
                                id="name"
                                required 
                                placeholder="e.g Non-Payment"
                                @focus="clearFieldError('name')"
                                :class="['border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['name']
                                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                                  : 'focus:ring-blue-500'
                                ]"
                              />
                              <FieldError :message="fieldErrors['name']"/>
                              <FieldError :message="fieldErrors['non_field_errors']"/>
                            </div>

                              <!-- NET INTERFACE  -->
                            <div class="flex flex-col">
                              <label for="region" class="mb-1 font-medium text-gray-700 dark:text-white">Network interface:<span class="text-red-500">*</span>:</label>
                              <GlassSelect
                                v-model="newVLAN.raw_device"
                                id="raw_device"
                                :options="netFields"
                                placeholder="Select network interface"
                                @focus="clearFieldError('raw_device')"
                                :error="!!fieldErrors['raw_device']"
                              />
                              <FieldError :message="fieldErrors['raw_device']"/>
                            </div>
                                
                            <!-- ADDRESS CIDR notation -->
                            <div class="flex flex-col">
                              <label for="description" class="mb-1 font-medium text-gray-700 dark:text-white">Address CIDR notation<span class="text-red-500">*</span>:</label>
                              <input 
                                v-model="newVLAN.ip_address"
                                id="ip_address"
                                required
                                @focus="clearFieldError('ip_address')"
                                placeholder="e.g 10.135.128.254/24"
                                :class="['border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['ip_address']
                                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                                  : 'focus:ring-blue-500'
                                ]"
                              />
                              <FieldError :message="fieldErrors['ip_address']"/>
                            </div>
                            
                            <!-- Gateway: -->
                            <div class="flex flex-col">
                              <label for="description" class="mb-1 font-medium text-gray-700 dark:text-white">Gateway:<span class="text-red-500">*</span>:</label>
                              <input 
                                v-model="newVLAN.gateway"
                                id="gateway"
                                required
                                @focus="clearFieldError('gateway')"
                                placeholder="e.g 10.135.128.225"
                                :class="['border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['gateway']
                                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                                  : 'focus:ring-blue-500'
                                ]"
                              />
                              <FieldError :message="fieldErrors['gateway']"/>
                            </div>
                            
                            <!-- Routes -->
                            <div class="flex flex-col col-span-1 md:col-span-2" v-for="(route, index) in newVLAN.routes" :key="index">
                              <label class="mb-2 font-medium text-gray-700 dark:text-white">Routes:</label>
                              
                              <!-- To and Via inputs side by side -->
                              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div class="flex flex-col">
                                  <label for="to" class="mb-1 font-medium text-gray-700 dark:text-white">To:</label>
                                  <input 
                                    v-model="route.to"
                                    id="to"
                                    @focus="clearFieldError('to')"
                                    placeholder="e.g 192.168.0.1"
                                    :class="[
                                      'border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', 
                                      fieldErrors['to'] ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                                    ]"
                                  />
                                  <FieldError :message="fieldErrors['to']"/>
                                </div>
                                
                                <div class="flex flex-col">
                                  <label for="via" class="mb-1 font-medium text-gray-700 dark:text-white">Via:</label>
                                  <input 
                                    v-model="route.via"
                                    id="via"
                                    @focus="clearFieldError('via')"
                                    placeholder="e.g 192.168.0.2"
                                    :class="[
                                      'border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', 
                                      fieldErrors['via'] ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                                    ]"
                                  />
                                  <FieldError :message="fieldErrors['via']"/>
                                </div>
                                <FieldError :message="fieldErrors['routes']"/>
                              </div>
                              
                              <!-- Add Route Button -->
                              <div class="mb-4">
                                <button 
                                  v-if="(addingItem && (canAddVLAN || user()?.is_staff)) || (!addingItem && (canChangeVLAN || user()?.is_staff))"
                                  type="button"
                                  @click.stop="addRoute({to: route.to, via: route.via})"
                                  @mousedown.prevent
                                  class="btn bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                                >
                                  Add Route
                                </button>
                              </div>
                              
                              <!-- Display all routes underneath -->
                              <div class="space-y-4">
                                <!-- Added Routes Section -->
                                <div v-show="routeAdded && routeArray.length > 0">
                                  <h4 class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                    New Routes
                                  </h4>
                                  <div class="flex flex-wrap gap-2">
                                    <div
                                      v-for="(item, idx) in routeArray" 
                                      :key="`route-${idx}`"
                                      class="group inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50/80 dark:bg-blue-900/20 backdrop-blur-md border border-blue-200 dark:border-blue-800 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                                    >
                                      <div class="flex items-center gap-1.5 text-sm">
                                        <span class="font-medium text-gray-900 dark:text-gray-100">{{ item.to }}</span>
                                        <svg class="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                        <span class="text-gray-600 dark:text-gray-400">{{ item.via }}</span>
                                      </div>
                                      <button 
                                        type="button"
                                        @click="removeRoute(idx, routeArray)"
                                        aria-label="Remove route"
                                        class="ml-1 p-0.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-all duration-200"
                                      >
                                        <svg 
                                          xmlns="http://www.w3.org/2000/svg" 
                                          class="h-4 w-4" 
                                          viewBox="0 0 20 20" 
                                          fill="currentColor"
                                        >
                                          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                <!-- Existing Routes Section -->
                                <div v-show="fetchedRoutes && fetchedRoutes.length > 0">
                                  <h4 class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                    Current Routes
                                  </h4>
                                  <div class="flex flex-wrap gap-2">
                                    <div
                                      v-for="(item, idx) in fetchedRoutes" 
                                      :key="`fetched-${idx}`"
                                      class="group inline-flex items-center gap-2 px-3 py-1.5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                                    >
                                      <div class="flex items-center gap-1.5 text-sm">
                                        <span class="font-medium text-gray-900 dark:text-gray-100">{{ item.to }}</span>
                                        <svg class="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                        <span class="text-gray-600 dark:text-gray-400">{{ item.via }}</span>
                                      </div>
                                      <button 
                                        v-if="(!addingItem && (canChangeVLAN || user()?.is_staff)) || (addingItem && (canAddVLAN || user()?.is_staff))"
                                        type="button"
                                        @click="removeRoute(idx, fetchedRoutes)"
                                        aria-label="Remove route"
                                        class="ml-1 p-0.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-all duration-200"
                                      >
                                        <svg 
                                          xmlns="http://www.w3.org/2000/svg" 
                                          class="h-4 w-4" 
                                          viewBox="0 0 20 20" 
                                          fill="currentColor"
                                        >
                                          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                <!-- Empty State -->
                                <div 
                                  v-show="(!routeAdded || !routeArray.length) && (!fetchedRoutes || !fetchedRoutes.length)"
                                  class="text-center py-6 px-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700"
                                >
                                  <svg class="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                  </svg>
                                  <p class="text-sm text-gray-500 dark:text-gray-400">No routes configured</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div class="flex justify-between items-center gap-2 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <div class="flex gap-2">
                              <button
                                v-if="(!addingItem && (canChangeVLAN || user()?.is_staff)) || (addingItem && (canAddVLAN || user()?.is_staff))"
                                type="button"
                                @click.stop="saveEdit(field.key)"
                                @mousedown.prevent
                                class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 px-4 py-2"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                @click.stop="cancelEdit"
                                @mousedown.prevent
                                class="btn bg-gray-500 text-white hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 px-4 py-2 "
                              >
                                Cancel
                              </button>
                            </div> 
                            <div>
                              <button 
                                v-show="!addingItem && (canDeleteVLAN || user()?.is_staff)"
                                type="button"
                                @click.stop="removeVlan(newVLAN.id, newVLAN)"
                                @mousedown.prevent
                                class="btn bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 px-4 py-2"
                              >
                                Delete
                              </button>
                            </div>                                           
                          </div>                       
                        </div>
                      </div>
                    </form>
                  </template>  
                  
                  <!-- Subnets -->
                  <template 
                    v-if="field.key === 'subnets'"
                    class="relative transition delay-150 duration-700">
                    <form @submit.prevent="saveEdit(field.key)">
                      <div class="flex fixed inset-0 items-center justify-center bg-black/50 backdrop-blur-sm z-40">
                        <div class="p-6 glass-card mb-2 w-[80%] h-[18%] shadow-xl z-50">
                          <h1 class="text-black dark:text-white">Subnet</h1>
                          <input 
                            type="text"
                            v-model="newSubnet.ip_address"
                            id="ip_address"
                            @focus="clearFieldError('ip_address')"
                            placeholder="e.g 11.82.14.0/24"
                            :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['ip_address']
                                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                                  : 'focus:ring-blue-500'
                                ]"
                            >
                            <FieldError :message="fieldErrors['ip_address']"/>
                          <div class="m-auto mt-2 flex justify-between">
                            <div class="space-x-1">
                              <button v-if="(!addingItem &&(canChangeSubnet || user()?.is_staff)) || ( addingItem && (canAddSubnet || user()?.is_staff))"
                                type="button"
                                @click="saveEdit(field.key)"
                                class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                Save
                              </button>
                              <button
                                type="button"
                                @click.stop="cancelEdit"
                                @mousedown.prevent
                                class="btn bg-gray-500 text-white hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                >
                                Cancel
                              </button>
                            </div>
                            <button v-if="!addingItem && (canDeleteSubnet || user()?.is_staff)"
                              type="button"
                              @click="removeSubnet(newSubnet.id, newSubnet)"
                              @mousedown.prevent
                              class="btn bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                              >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div> 
                    </form>
                  </template>

                  <!-- SNMP -->
                  <template 
                    v-if="field.key == 'snmp_targets'"
                    class="relative">
                    <form @submit.prevent="saveEdit(field.key)">
                      <div class="absolute fixed inset-0 flex items-center justify-center bg-gray-600 bg-opacity-50">
                        <div class="bg-white p-6 dark:bg-gray-800 dark:border-gray-800 rounded-lg shadow-sm border border-gray-200 mb-2 w-[80%] h-[18%] shadow-xl z-50">
                          <h1 class="text-black dark:text-white">SNMP Targets</h1>
                          <input 
                            type="text"
                            v-model="editValue"
                            id="ip_address"
                            placeholder="e.g 11.70.158.1"
                            class="dark:bg-gray-700"
                            >
                          <div class="mt-2 flex justify-between">
                            <div class="space-x-1">
                              <button v-if="canChangeProbe || user()?.is_staff"
                                type="button"
                                @click="saveEdit(field.key)"
                                class="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                                >
                                Save
                              </button>
                              <button
                                type="button"
                                @click.stop="cancelEdit"
                                @mousedown.prevent
                                class="px-3 py-1 bg-gray-500 text-white hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded-r"
                                >
                                Cancel
                              </button>
                            </div>
                            <button 
                              v-if="!addingItem"
                              type="button"
                              @click="removeTarget"
                              @mousedown.prevent
                              class="px-3 py-1 bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 rounded-r"
                              >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div> 
                    </form>
                  </template>

                  <template v-else-if="field.type === 'array'">
                    <textarea
                      v-model="editValue"
                      @keyup.enter="saveEdit(field.key)"
                      class="border border-gray-300 rounded-l px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 flex-grow"
                    ></textarea>
                  </template>
                  <template v-else-if="field.type === 'boolean'">
                    <GlassSelect
                      v-model="editValue"
                      :options="[{id:true,name:'Yes'},{id:false,name:'No'}]"
                    />
                  </template>
                  <template v-else-if="field.type === 'select'">
                    <GlassSelect
                      v-model="editValue"
                      :options="netFields"
                      placeholder="Select an interface"
                    />
                  </template>
                  <template v-else>
                    <div class="w-full flex-grow">
                      <input
                        v-model="editValue"
                        :type="field.type"
                        @keyup.enter="saveEdit(field.key)"
                        @focus="clearFieldError(field.key)"
                        :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors[field.key]
                          ? 'ring-2 ring-red-500  focus:ring-red-500'
                          : 'focus:ring-blue-500'
                        ]"
                      />
                      <FieldError :message="fieldErrors[field.key]"/>
                    </div>
                  </template>
                  <div class="flex pl-2 space-x-2 flex-shrink-0">
                    <button
                      type="button"
                      @click.stop="saveEdit(field.key)"
                      @mousedown.prevent
                      class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                      Save
                    </button>
                    <button
                      type="button"
                      @click.stop="cancelEdit"
                      @mousedown.prevent
                      class="btn bg-gray-500 text-white hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                      Cancel
                    </button>
                  </div>
                </div>
              </template>

                <template v-else-if="handleNoEdit(field.key)"
                  class="flex space-between"
                >
                <!-- Dropdown Selects -->
                <div  class="relative w-full"
                      v-if="(field.key==='vlans' && (canViewVLAN || user()?.is_staff)) 
                      || (field.key==='subnets' && (canViewSubnet || user()?.is_staff))"
                >
                  <div @mouseenter="showItems[field.key] = true, hoveritem = field.key"
                    @mouseleave="showItems[field.key] = false">
                    <div
                      @change="displayDropDown(field.key)"
                      class="w-full text-white bg-gray-800 dark:bg-gray-200 dark:hover:bg-gray-200 dark:text-gray-900 hover:bg-gray-800 font-medium text-sm px-5 py-1 text-center inline-flex rounded-t">
                      {{ field.key === 'vlans' ? 'Manage Probe VLANs' : 'Manage Probe Subnets' }}
                      <svg
                      class="ml-auto h-5 w-5 transform transition-transform duration-300"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      :class="{'rotate-180': showItems[field.key] }"
                      >
                      <path
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                      </svg>
                    </div>
                    <ul v-show="showItems[field.key]" class="w-full text-white dark:text-gray-900 border-gray-300 dark:bg-gray-200 bg-gray-800 absolute shadow-lg z-10 overflow-y-auto rounded-b">
                      <li 
                        v-for="(item) in field.key === 'vlans' ? fetchedVlans : fetchedSubnets"
                        :key="item.name || item.ip_address"
                        class="w-full px-4 py-2 hover:bg-gray-600 cursor-pointer dark:hover:text-gray-50 text-sm font-medium"
                        @click="field.key === 'vlans' ? editVlan(field.key, item.name) : editSubnet(field.key, item.ip_address)"
                      >
                      {{ field.key === 'vlans' ? item.name : item.ip_address }}
                      </li>
                    </ul>
                  </div>
                </div>   

                <!-- SNMP Targets Dropdown -->
                <div v-if="['snmp_targets'].includes(field.key)" class="relative w-full z-10">
                  <div @mouseenter="showItems[field.key] = true, hoveritem = field.key"
                    @mouseleave="showItems[field.key] = false">
                    <div
                      @change="displayDropDown(field.key)"
                      class="w-full text-white bg-gray-800 hover:bg-gray-800 dark:bg-gray-200 dark:hover:bg-gray-200 dark:text-gray-900 font-medium text-sm px-5 py-1 text-center inline-flex rounded-t">
                      {{ 'Manage Probe Targets' }}
                      <svg
                      class="ml-auto h-5 w-5 transform transition-transform duration-300"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      :class="{'rotate-180': showItems[field.key] }"
                      >
                      <path
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                      </svg>
                    </div>
                    <ul v-show="showItems[field.key]" class="w-full text-white dark:text-gray-900 border-gray-300 dark:bg-gray-200 bg-gray-800 absolute shadow-lg z-10 overflow-y-auto rounded-b">
                      <li 
                        v-for="(item) in probe.snmp_targets"
                        class="w-full px-4 py-2 hover:bg-gray-600 dark:hover:text-gray-50 cursor-pointer text-sm font-medium"
                        @click="editTarget(field.key,item)"
                      >
                      {{  item }}
                      </li>
                    </ul>
                  </div>
                </div>   

                <span v-else
                  class="flex-grow"
                  @click="startEdit(field.key)"
                  v-if="!['subnets', 'vlans', 'snmp_targets'].includes(field.key)"
                  >
                  {{ formatValue(field.key, probe[field.key]) }}
                </span>

                <!-- Displays icon for edit or add  -->
                <div class="relative dark:text-white dark:text-white">
                  <button v-if="(field.key==='vlans' && (canAddVLAN || user()?.is_staff)) || (field.key==='subnets' && (canAddSubnet || user()?.is_staff))"
                    @click="startEdit(field.key)"
                    class="ml-2 p-1 text-gray-400 hover:text-blue-500 dark:text-blue-500 dark:hover:text-white transition-colors duration-150"
                    @mouseover="showToolTip=true, hoveritem=field.key"
                    @mouseleave="showToolTip=false"
                    :id=field.key
                    >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg"
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none" stroke="currentColor" 
                      stroke-width="2" stroke-linecap="round" 
                      stroke-linejoin="round" 
                      class="lucide lucide-copy-plus">
                      <line x1="15" x2="15" y1="12" y2="18"/>
                      <line x1="12" x2="18" y1="15" y2="15"/>
                      <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
                    </svg>
                  </button>
                  <button v-if="field.key==='snmp_targets' && (canChangeProbe || user()?.is_staff)"
                    @click="startEdit(field.key)"
                    class="ml-2 p-1 text-gray-400 hover:text-blue-500 dark:text-blue-500 dark:hover:text-white transition-colors duration-150"
                    @mouseover="showToolTip=true, hoveritem=field.key"
                    @mouseleave="showToolTip=false"
                    :id=field.key
                    >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg"
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none" stroke="currentColor" 
                      stroke-width="2" stroke-linecap="round" 
                      stroke-linejoin="round" 
                      class="lucide lucide-copy-plus">
                      <line x1="15" x2="15" y1="12" y2="18"/>
                      <line x1="12" x2="18" y1="15" y2="15"/>
                      <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
                    </svg> 
                  </button>
                  <button 
                    v-else-if="!['subnets', 'vlans', 'snmp_targets'].includes(field.key) && (canChangeProbe || user()?.is_staff)"
                    @click="startEdit(field.key)"
                    class="ml-2 p-1 text-gray-400 hover:text-blue-500 dark:text-blue-500 dark:hover:text-white transition-colors duration-150"
                    @mouseover="showToolTip=true, hoveritem=field.key"
                    @mouseleave="showToolTip=false"
                    :id=field.key
                    >
                    <svg
                      class="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg> 
                  </button>
                  <span 
                    v-if="showToolTip && hoveritem === field.key"
                    class="absolute bottom-full whitespace-nowrap mb-2 transform -translate-x-1/2 rounded py-1 px-2 dark:shadow-lg dark:bg-gray-500 bg-gray-800 bg-gray-800 text-white text-center"
                        >
                    {{(['subnets', 'vlans', "snmp_targets"].includes(field.key)) ? 'Add ' : 'Change ' }}{{ field.label}}
                  </span>
                </div>
                </template>
              </div>
            </td>
            <td class="py-4 px-4">
              {{ getPendingValue(field.key) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="activeTab === 'wifi' && (canViewProbeWifi || user()?.is_staff)" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-2 mb-6">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold dark:text-white">Scan for Nearby Networks</h2>
        <button
          v-if="canAddProbeWifi || user()?.is_staff"
          @click="runWiFiScan"
          :disabled="wifiScanLoading"
          class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm disabled:opacity-50"
        >
          {{ wifiScanLoading ? 'Scanning...' : 'Scan for SSIDs' }}
        </button>
      </div>
      <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Open networks are excluded. Profiles can be created for WPA2-PSK, WPA-Enterprise, and WPA3-SAE.
      </p>
      <div v-if="wifiScanError" class="text-sm text-red-600 dark:text-red-400 mb-3">{{ wifiScanError }}</div>
      <table v-if="wifiScanResults.length" class="w-full text-sm dark:text-white mb-4">
        <thead>
          <tr class="border-b border-gray-200 dark:border-gray-700">
            <th class="text-left py-2">SSID</th>
            <th class="text-left py-2">BSSID</th>
            <th class="text-left py-2">Signal (dBm)</th>
            <th class="text-left py-2">Encryption</th>
            <th class="text-right py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="net in wifiScanResults" :key="net.bssid" class="border-b border-gray-100 dark:border-gray-700">
            <td class="py-2">{{ net.ssid }}</td>
            <td class="py-2 font-mono text-xs">{{ net.bssid }}</td>
            <td class="py-2">{{ net.signal_dbm }}</td>
            <td class="py-2">{{ formatScanEncryption(net.encryption) }}</td>
            <td class="py-2 text-right">
              <button
                v-if="canAddProbeWifi || user()?.is_staff"
                @click="createProfileFromScan(net)"
                class="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-xs"
              >Create Profile</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else-if="!wifiScanLoading && !wifiScanError && wifiScanRan" class="text-gray-500 dark:text-gray-400 text-sm mb-4">
        No matching networks found in range.
      </p>

      <div class="flex items-center justify-between mb-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
        <h2 class="text-lg font-semibold dark:text-white">Attached WiFi Profiles</h2>
        <button
          v-if="canAddProbeWifi || user()?.is_staff"
          @click="showAttachModal = true"
          class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
        >
          Attach Profile
        </button>
      </div>
      <table v-if="wifiAssignments.length" class="w-full text-sm dark:text-white">
        <thead>
          <tr class="border-b border-gray-200 dark:border-gray-700">
            <th class="text-left py-2">Profile</th>
            <th class="text-left py-2">SSID</th>
            <th class="text-left py-2">Auth</th>
            <th class="text-center py-2">Active</th>
            <th class="text-right py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in wifiAssignments" :key="a.id" class="border-b border-gray-100 dark:border-gray-700">
            <td class="py-2">{{ a.wifi_profile_name }}</td>
            <td class="py-2">{{ a.wifi_profile_ssid }}</td>
            <td class="py-2">{{ a.wifi_profile_auth_type }}</td>
            <td class="py-2 text-center">
              <input
                type="checkbox"
                :checked="a.is_active"
                :disabled="!(canChangeProbeWifi || user()?.is_staff)"
                @change="toggleWiFiActive(a)"
              />
            </td>
            <td class="py-2 text-right">
              <button
                v-if="canDeleteProbeWifi || user()?.is_staff"
                @click="detachWiFiProfile(a.id)"
                class="text-red-600 hover:underline"
              >Detach</button>
              <span v-else class="text-xs text-gray-400 italic">Read only</span>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="text-gray-500 dark:text-gray-400 text-sm">No WiFi profiles attached to this probe.</p>
    </div>

    <div v-if="showAttachModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4 dark:text-white">Attach WiFi Profile</h3>
        <select v-model="selectedProfileToAttach" class="w-full border border-gray-300 dark:bg-gray-600 dark:text-white rounded px-3 py-2 mb-4">
          <option :value="null">Select a profile...</option>
          <option v-for="p in availableProfiles" :key="p.id" :value="p.id">{{ p.name }} ({{ p.ssid }})</option>
        </select>
        <div class="flex justify-end space-x-2">
          <button @click="showAttachModal = false" class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">Cancel</button>
          <button @click="attachWiFiProfile" :disabled="!selectedProfileToAttach" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">Attach</button>
        </div>
      </div>
    </div>

      <!-- SNMP Tab -->
    <SNMPTab
      v-if="activeTab === 'snmp' && permsLoaded"
      parent-type="probe"
      :parent-id="probe.id"
      :org-id="probe.org"
      :class="'p-6'"
    />
    </div>

  <!-- Error Message -->
  <PageError v-else-if="errorMessage"
    :error="errorMessage"
    :errorCode="errorCode"
  />

  <div v-else>
    <p>Loading...</p>
  </div>

</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { getProbeById, updateProbe , getAllProbes, scanProbeWiFi} from "@/services/probeService";
import { createVlan, getAllVlans, updateVlan, deleteVlan } from "@/services/vlanService";
import { createSubnet, getAllSubnets, updateSubnet, deleteSubnet } from "@/services/subnetService";
import {
  getProbeWiFiProfiles,
  getAllWiFiProfiles,
  attachWiFiProfileToProbe,
  updateProbeWiFiProfile,
  detachWiFiProfileFromProbe,
} from "@/services/wifiProfileService";
import { useStore } from "@/store";
import FieldError from "@/components/FieldError.vue";
import { formatError } from "@/utils/errorMessages";
import PageError from "@/components/PageError.vue";
import ExplainConfigButton from "@/components/ExplainConfigButton.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useRouter } from "vue-router";
import { isValidIPv4OrSubnet} from "@/utils/networkValidation";
import { useTutorial } from "@/composables/tutorial";
import SNMPTab from "@/components/SiteDetail/SnmpTab.vue";
import GlassSelect from "@/components/GlassSelect.vue";


export default {
  components: {
    FieldError,
    PageError,
    ExplainConfigButton,
    SNMPTab,
    GlassSelect
  },
  setup() {
    const activeTab = ref("probeDetails");
    const tenant = useMultiTenant();
    const router = useRouter();
    const showToolTip = ref(null)
    const probe = ref(null);
    const editingField = ref(null);
    const editValue = ref(null);
    const noEditFields = ["name", "last_modified","created_date"];
    const netFields = ['eth0', 'eth1', 'eth2', 'eth3'];
    const newSubnet = ref({
      ip_address: null,
      probe: null,
      },);
    const newVLAN = ref({
      name: null,
      vlan_id: 1,
      gateway: null,
      ip_address: null,
      routes: [
      {  to: null ,
        via: null
      }
      ],
      raw_device: "",
      probe: null
      },);
    const fetchedRoutes = ref([])
    const fetchedVlans = ref([]);
    const fetchedSubnets = ref([]);
    const routeArray = ref([]);
    const permsLoaded = computed(() => useStore.permissionsLoadedRef.value);
    const targets = ref([])
    const routeAdded = ref(false);
    const selectedVLAN = ref(null);
    const selectedSubnet = ref(null);
    const selectedTarget = ref(null);
    const showDropDown = ref({ vlans: false, subnets: false, snmp_targets: false });
    const addingItem = ref(false);
    const deletingItem = ref(false);
    const canChangeProbe = ref(true);
    const canChangeVLAN = ref(true);
    const canChangeSubnet= ref(true);
    const canViewVLAN = ref(true);
    const canViewSubnet = ref(true);
    const canDeleteVLAN = ref(true);
    const canDeleteSubnet = ref(true);
    const canAddVLAN = ref(true);
    const canAddSubnet = ref(true);
    const fieldErrors = ref({});
    const errorMessage = ref(null);
    const errorCode = ref(null);
    const canAddProbeWifi = ref(false);
    const canChangeProbeWifi = ref(false);
    const canDeleteProbeWifi = ref(false);
    const canViewProbeWifi = ref(false);
    const wifiAssignments = ref([]);
    const availableProfiles = ref([]);
    const showAttachModal = ref(false);
    const selectedProfileToAttach = ref(null);
    const wifiScanLoading = ref(false);
    const wifiScanResults = ref([]);
    const wifiScanError = ref("");
    const wifiScanRan = ref(false);
    const SCAN_ENCRYPTION_LABELS = {
      wpa_psk: "WPA2-PSK",
      wpa_enterprise: "WPA-Enterprise",
      wpa3_sae: "WPA3-SAE",
    };
    const formatScanEncryption = (e) => SCAN_ENCRYPTION_LABELS[e] || e || "Unknown";
    const displayFields = [
      { key: "name", label: "Name", type: "text" },
      { key: "is_approved", label: "Is Approved", type: "boolean" },
      { key: "is_installed", label: "Is Installed", type: "boolean" },
      { key: "is_wifi_probe", label: "WiFi Probe", type: "boolean" },
      { key: "in_maintenance", label: "In Maintenance", type: "boolean" },
      { key: "device", label: "Device", type: "text" },
      { key: "dns_one", label: "DNS Primary", type: "text" },
      { key: "dns_two", label: "DNS Secondary", type: "text" },
      { key: "dns_three", label: "DNS Tertiary", type: "text" },
      { key: "ntp", label: "NTP", type: "text" },
      { key: "vlans", label: "VLANs", type: "array" },
      { key: "subnets", label: "Subnets", type: "subnets" },  
      { key: "api_key", label: "API Key", type: "password" },
      { key: "api_address", label: "API Address", type: "text" },
      { key: "api_port", label: "API Port", type: "number" },
      { key: "approvedprobe", label: "Approved Probe", type: "boolean" },
      { key: "pending", label: "Pending", type: "boolean" },
      { key: "span_interface", label: "Span Interface", type: "text" },
      { key: "snmp_targets", label: "SNMP Targets", type: "array" },
      { key: "created_date", label: "Created Date", type: "text" },
      { key: "last_modified", label: "Last Modified", type: "text" },
    ];

    const defaultSubnet = {
      ip_address: null,
      probe: null,
      id: null,
      created_date: '',
      last_modified: ''
    };

    const defaultVlan = {
      name: null,
      gateway: null,
      ip_address: null,
      routes: [
        { to: null, via: null }
      ],
      raw_device: "",
      probe: null,
      id: null,
      created_date: '',
      last_modified: ''
    };

    const user = () => {
      return useStore.getUser()
    };

    const clearFieldError = (fieldName) => {
      if (fieldErrors.value[fieldName]) {
        delete fieldErrors.value[fieldName];
      }
    };

    watch(
      () => useStore.getUser(),
      async (user) => {
        if (user) {

          canChangeProbe.value = await useStore.hasPermission("Can change probe");

          canChangeVLAN.value = await useStore.hasPermission("Can change VLAN");
          canChangeSubnet.value = await useStore.hasPermission("Can change subnet");

          canAddVLAN.value = await useStore.hasPermission("Can add VLAN");
          canAddSubnet.value = await useStore.hasPermission("Can add subnet");

          canViewVLAN.value = await useStore.hasPermission("Can view VLAN");
          canViewSubnet.value = await useStore.hasPermission("Can view subnet");

          canDeleteVLAN.value = await useStore.hasPermission("Can delete VLAN");
          canDeleteSubnet.value = await useStore.hasPermission("Can delete subnet");
          canViewProbeWifi.value = await useStore.hasPermission("Can view Probe WiFi Profile Assignment");
          canAddProbeWifi.value = await useStore.hasPermission("Can add Probe WiFi Profile Assignment");
          canChangeProbeWifi.value = await useStore.hasPermission("Can change Probe WiFi Profile Assignment");
          canDeleteProbeWifi.value = await useStore.hasPermission("Can delete Probe WiFi Profile Assignment");
        }
      },
      { immediate: true }
    );

    const fetchProbe = async (schemaName = null) => {
      const probeId = window.location.pathname.split("/").pop();
      try {
        const probes = await getAllProbes(schemaName);
        probe.value = probes.find(item => item.id == probeId);
      } catch (error) {
        console.error("Error fetching probe:", error);
        const { code, message } = formatError(error);
        errorCode.value = code;
        errorMessage.value = message;
      }
    };

    const fetchVlans = async (schemaName = null) => {
      try {
        const probeId = window.location.pathname.split("/").pop();
        const response = await getAllVlans(schemaName);
        const relevantToProbe = response.filter((item) => item.probe == probeId);
        fetchedVlans.value = relevantToProbe;
      } catch (error) {
        console.error("Error fetching vlans:", error);
      }
      
    };


    const fetchSubnets = async (schemaName = null) => {
      try {
        const probeId = window.location.pathname.split("/").pop();
        const response = await getAllSubnets(schemaName);
        const relevantToProbe = response.filter((item) => item.probe == probeId);
        fetchedSubnets.value = relevantToProbe;
      } catch (error) {
        console.error("Error fetching subnets:", error);
      }
      
    };
    const goBack = () => {
      router.push({
        name: 'Probes',
        query: tenant.addSchemaParam()
      });
    };
    const startEdit = (key) => {
      if (canChangeProbe.value || user()?.is_staff || canAddVLAN.value || canAddSubnet.value ){
        if (handleNoEdit(key)){
          editingField.value = key;
          if (key !== 'snmp_targets') editValue.value = probe.value[key]; 
          if (key === 'vlans' || key === 'subnets') (addingItem.value = true) 
          showToolTip.value = false;
          addingItem.value = true;
        }
    }
    };

    const editVlan = (key, targetName) => {
      editingField.value = key;
      selectedVLAN.value = fetchedVlans.value.find(item => item.name === targetName);

      if (selectedVLAN.value) {
        fetchedRoutes.value = selectedVLAN.value.routes?.length ? selectedVLAN.value.routes : [];
        Object.assign(newVLAN.value, selectedVLAN.value);
        newVLAN.value.routes = [{ to: null, via: null }];
      }
      showDropDown.value[key] = false;
    };

    const editSubnet = (key, targetIP) => {
      editingField.value = key;
      selectedSubnet.value = fetchedSubnets.value.find(item => item.ip_address === targetIP);

      if (selectedSubnet.value) Object.assign(newSubnet.value, selectedSubnet.value);
      showDropDown.value[key] = false;
    };

    const editTarget = (key, target) => {
      editingField.value = key;
      targets.value = probe.value.snmp_targets;
      selectedTarget.value = targets.value.find(item => item === target);
      editValue.value = selectedTarget.value
      showDropDown.value[key] = false;
    };

    const removeTarget = () => {
      const index = probe.value.snmp_targets.indexOf(selectedTarget.value);
      probe.value.snmp_targets.splice(index,1)
      deletingItem.value = true
      editValue.value = null
    };

    const addRoute = (route) => {
      fieldErrors.value = {};

      if (!route?.to) {
        fieldErrors.value['to'] = '"To" field is required.';
        return;
      }
      if (!isValidIPv4OrSubnet(route.to)) {
        fieldErrors.value['to'] = 'Please enter a valid IPv4 address (e.g., 192.168.1.1 or 192.168.1.0/24).';
        return;
      }

      if (!route?.via) {
        fieldErrors.value['via'] = '"Via" field is required.';
        return;
      }
      if (!isValidIPv4OrSubnet(route.via)) {
        fieldErrors.value['via'] = 'Please enter a valid IPv4 address (e.g., 192.168.1.1 or 192.168.1.0/24).';
        return;
      }


      const obj = {...route }
      routeArray.value.push(obj);
      routeAdded.value = true;
      newVLAN.value.routes = [{ to: null, via: null }]; 

    };

    const removeRoute = (index, array) => {
      array.splice(index,1)
    };

    const removeSubnet = async (id, subnet) => {
      try{
        const validation = tenant.validateSchemaForOperation();
        if (!validation.valid){
          error.value = "Malformed request. Failed to delete subnet!";
          errorCode.value = "422!";
          return;
        }
        const response = await deleteSubnet(id, validation.schemaName);
        editingField.value = null;
        Object.assign(newSubnet.value, defaultSubnet);
        fetchedSubnets.value = fetchedSubnets.value.filter((item) => item.id !== id);
      }catch(error){
        console.error("Error deleting subnet:", error);
      }
    };

    const removeVlan = async (id, vlan) => {
      try{
        const validation = tenant.validateSchemaForOperation();
        if (!validation.valid){
          error.value = "Malformed request. Failed to delete VLAN!";
          errorCode.value = "422!";
          return;
        }
        const response = await deleteVlan(id, validation.schemaName);
        editingField.value = null;
        Object.assign(newVLAN.value, defaultVlan);
        fetchedVlans.value = fetchedVlans.value.filter((item) => item.id !== id);
      }catch(error){
        console.error("Error deleting vlan:", error);
      }
    };

    const handleNoEdit = (key) => {
        return !noEditFields.includes(key)
    };

    const cancelEdit = () => {
      editingField.value = null;
      editValue.value = null;
      fieldErrors.value = {};

      // Reset fields
      Object.assign(newSubnet.value, defaultSubnet);
      Object.assign(newVLAN.value, defaultVlan);

      routeArray.value = [];
      fetchedRoutes.value = [];
      addingItem.value = false;
    };

    const handleVlanUpdate = async (schemaName = null) => {
      const savedVlan = { ...newVLAN.value };
      savedVlan.routes = fetchedRoutes.value.concat(routeArray.value)
  
      if (!newVLAN.value?.id) {
        savedVlan.probe = probe.value.id;
        await createVlan(savedVlan, schemaName);
      } else {
        await updateVlan(savedVlan.id, savedVlan, schemaName);
      }

      Object.assign(newVLAN.value, defaultVlan);
      fetchVlans(schemaName);
      addingItem.value = false;
    };

    const handleSubnetUpdate = async (schemaName = null) => {
      if (!newSubnet.value?.id) {
        newSubnet.value.probe = probe.value.id;
        await createSubnet(newSubnet.value, schemaName);
      } else {
        await updateSubnet(newSubnet.value.id, newSubnet.value, schemaName);
      }

      Object.assign(newSubnet.value, defaultSubnet);
      fetchSubnets(schemaName);
      addingItem.value = false;
    };

    const handleSNMPTarget = () => {
      probe.value.snmp_targets = probe.value.snmp_targets ?? [];

      if (addingItem.value){
        editValue.value = probe.value.snmp_targets.concat(editValue.value.split(",").map((item) => item.trim()));
      }
      else if(deletingItem.value) {
        editValue.value = probe.value.snmp_targets
        }
      else{
        const index = probe.value.snmp_targets.indexOf(selectedTarget.value);
        probe.value.snmp_targets.splice(index,1);
        editValue.value = probe.value.snmp_targets.concat(editValue.value.split(",").map((item) => item.trim())); 
      }
    }

    const saveEdit = async (key) => {
      try {
        const validation = tenant.validateSchemaForOperation();
        if (!validation.valid){
          error.value = "Malformed request. Failed to update probe!";
          errorCode.value = "422!";
          return;
        }

        fieldErrors.value = {};

        if (key == 'snmp_targets'){
          handleSNMPTarget(validation.schemaName);
        }
        // Create the update payload
        const updatedData = {
          ...probe.value, // Include current probe data
          [key]: editValue.value, // Update the specific field
        };

        // Remove any undefined or null values
        Object.keys(updatedData).forEach((k) => {
          if (updatedData[k] === undefined || updatedData[k] === null) {
            delete updatedData[k];
          }
        });

        // retain dns_two and dns_three as empty string and send with request if changed to null
        Object.keys(updatedData).forEach(key => {
          if (updatedData[key] === '' && (key == "dns_two" || key == "dns_three") ) {
            updatedData[key] = null;
          }
        });

        if (key === "vlans") {
          await handleVlanUpdate(validation.schemaName);
        }
        // Handle Subnet updates
        else if (key === "subnets") {
          await handleSubnetUpdate(validation.schemaName);
        }
        
        // Make the API call
        const response = await updateProbe(
          probe.value.id, 
          updatedData, 
          validation.schemaName
        );  
        
        // Update the local state
        probe.value = response;
        editingField.value = null;
        editValue.value = null;   
        routeArray.value = [];

      } catch (error) {

        const errData = error?.response?.data;
        if (errData) {
          for (const key in errData) {
            const value = errData[key];

            if (Array.isArray(value)) {
              fieldErrors.value[key] = value[0];
            } 
            else if (typeof value === 'object' && value !== null) {
              const nestedFirstKey = Object.keys(value)['non_field_errors'];
              const nestedMessages = value[nestedFirstKey];

              if (Array.isArray(nestedMessages)) {
                fieldErrors.value[key] = nestedMessages['non_field_errors'];
              }
            }
          }
        }

        console.error("Error updating probe:", error);
        
      }
    };

    const isFieldEdited = (key) => {
      return probe.value?.pending && probe.value.pending[key] !== undefined;
    };

    const handleHash = (value) => {
      const unhashby = 5;

      if (value.length <= 2 * unhashby) {
        return value;
      }

      const first = value.slice(0,unhashby);
      const last = value.slice(-unhashby);
      const hashed = "*".repeat(value.length - 2*unhashby);
      return first.concat(hashed,last);
    }

    const formatValue = (key, value) => {
      if (value === undefined || value === null) return "-";
      if (Array.isArray(value)) return value.join(", ");
      if (typeof value === "boolean") return value ? "Yes" : "No";
      if (key === "last_modified" || key === "created_date") {
        return new Date(value).toLocaleString();
      }
      if (key == 'api_key' && value){
        return handleHash(value)
      }
      return value
    };

    const getPendingValue = (key) => {
      if (!probe.value?.pendingprobe || !probe.value?.orginalprobe) return "-";

      try {
        const pendingData = JSON.parse(probe.value.pendingprobe);
        const originalData = JSON.parse(probe.value.orginalprobe);

        // If the values are different, return the pending value
        if (
          JSON.stringify(pendingData[key]) !== JSON.stringify(originalData[key])
        ) {
          return formatValue(key, pendingData[key]);
        }
        return "-";
      } catch (error) {
        console.error("Error parsing probe data:", error);
        return "-";
      }
    };

    const displayDropDown = (key) => {
      showDropDown.value[key] = !showDropDown.value[key];
    };

    const loadWiFiAssignments = async (schemaName = null) => {
      if (!probe.value?.id) return;
      try {
        wifiAssignments.value = await getProbeWiFiProfiles(probe.value.id, schemaName);
        availableProfiles.value = await getAllWiFiProfiles(schemaName);
      } catch (e) {
        console.error("Error loading WiFi assignments:", e);
      }
    };

    const toggleWiFiActive = async (assignment) => {
      const validation = tenant.validateSchemaForOperation();
      if (!validation.valid) return;
      wifiScanError.value = "";
      try {
        await updateProbeWiFiProfile(assignment.id, { is_active: !assignment.is_active }, validation.schemaName);
      } catch (e) {
        wifiScanError.value = e?.response?.data?.detail || "Failed to update the WiFi profile assignment.";
        console.error("toggleWiFiActive failed:", e);
      } finally {
        // Always re-sync from the server so the UI reflects actual state.
        await loadWiFiAssignments(validation.schemaName);
      }
    };

    const attachWiFiProfile = async () => {
      if (!selectedProfileToAttach.value) return;
      const validation = tenant.validateSchemaForOperation();
      if (!validation.valid) return;
      wifiScanError.value = "";
      try {
        await attachWiFiProfileToProbe(
          { probe: probe.value.id, wifi_profile: selectedProfileToAttach.value, is_active: true },
          validation.schemaName,
        );
        showAttachModal.value = false;
        selectedProfileToAttach.value = null;
      } catch (e) {
        wifiScanError.value = e?.response?.data?.detail || "Failed to attach the WiFi profile.";
        console.error("attachWiFiProfile failed:", e);
      } finally {
        await loadWiFiAssignments(validation.schemaName);
      }
    };

    const detachWiFiProfile = async (assignmentId) => {
      const validation = tenant.validateSchemaForOperation();
      if (!validation.valid) return;
      wifiScanError.value = "";
      try {
        await detachWiFiProfileFromProbe(assignmentId, validation.schemaName);
      } catch (e) {
        wifiScanError.value = e?.response?.data?.detail || "Failed to detach the WiFi profile.";
        console.error("detachWiFiProfile failed:", e);
      } finally {
        await loadWiFiAssignments(validation.schemaName);
      }
    };

    const runWiFiScan = async () => {
      if (!probe.value?.id) return;
      const validation = tenant.validateSchemaForOperation();
      if (!validation.valid) return;
      wifiScanLoading.value = true;
      wifiScanError.value = "";
      wifiScanResults.value = [];
      wifiScanRan.value = false;
      try {
        const data = await scanProbeWiFi(probe.value.id, validation.schemaName);
        wifiScanResults.value = Array.isArray(data?.networks) ? data.networks : [];
        wifiScanRan.value = true;
      } catch (e) {
        const payload = e?.response?.data || {};
        if (payload.error === "script_missing") {
          wifiScanError.value = "Unable to run WiFi scan, please commit the probe changes first and ensure WiFi Probe is set to Yes under Probe Details.";
        } else if (payload.error === "no_wireless_interface") {
          wifiScanError.value = "Probe has no wireless interface available.";
        } else if (payload.error === "scan_failed") {
          wifiScanError.value = `Scan failed on probe: ${payload.detail || "unknown error"}`;
        } else if (e?.response?.status === 504) {
          wifiScanError.value = "Probe did not respond. Check that the device is online.";
        } else if (e?.response?.status === 403) {
          wifiScanError.value = "You do not have permission to scan WiFi.";
        } else {
          wifiScanError.value = payload.error || payload.detail || "Failed to scan for WiFi networks.";
        }
      } finally {
        wifiScanLoading.value = false;
      }
    };

    const createProfileFromScan = (network) => {
      const validation = tenant.validateSchemaForOperation();
      if (!validation.valid) return;
      router.push({
        name: "AddWiFiProfile",
        query: {
          ssid: network.ssid,
          auth_type: network.encryption,
          schemaName: validation.schemaName,
        },
      });
    };

    onMounted(async () => {
      const validation = tenant.validateSchemaForOperation();
      if (!validation.valid) {
        errorMessage.value = "Malformed request. Failed to get probe details!";
        errorCode.value = "422!";
        return;
      }
      await fetchProbe(validation.schemaName);
      fetchVlans(validation.schemaName);
      fetchSubnets(validation.schemaName);
      if (probe.value) {
        await loadWiFiAssignments(validation.schemaName);
      }
    });

    const { start: _start } = useTutorial();

    const probeDetailSteps = [
      { element: '#probe-field-name', action: 'display', message: 'The name of this probe.' },
      { element: '#probe-field-device', action: 'display', message: 'Click the edit icon to update the device identifier.' },
      { element: '#probe-field-is_approved', action: 'display', message: 'Click the edit icon to change the approval status.' },
      { element: '#probe-field-is_installed', action: 'display', message: 'Click the edit icon to change the installation status.' },
      { element: '#probe-field-in_maintenance', action: 'display', message: 'Click the edit icon to toggle maintenance mode.' },
      { element: '#probe-field-dns_one', action: 'display', message: 'Click the edit icon to update the primary DNS server.' },
      { element: '#probe-field-vlans', action: 'display', message: 'Use the VLAN manager to add or edit probe VLANs.' },
      { element: '#probe-field-subnets', action: 'display', message: 'Use the subnet manager to add or edit probe subnets.' },
      { element: '#probe-field-created_date', action: 'display', message: 'The date and time this probe was created.' },
    ];

    const startTutorial = async () => {
      await _start(probeDetailSteps);
    };

    return {
      activeTab,
      canViewProbeWifi,
      canAddProbeWifi,
      canChangeProbeWifi,
      canDeleteProbeWifi,
      wifiAssignments,
      availableProfiles,
      showAttachModal,
      selectedProfileToAttach,
      loadWiFiAssignments,
      toggleWiFiActive,
      attachWiFiProfile,
      detachWiFiProfile,
      wifiScanLoading,
      wifiScanResults,
      wifiScanError,
      wifiScanRan,
      formatScanEncryption,
      runWiFiScan,
      createProfileFromScan,
      tenant,
      probe,
      editingField,
      editValue,
      displayFields,
      goBack,
      startEdit,
      cancelEdit,
      saveEdit,
      isFieldEdited,
      formatValue,
      getPendingValue,
      showToolTip,
      handleNoEdit,
      netFields,
      newVLAN,
      fetchedVlans,
      editVlan,
      fetchedSubnets,
      editSubnet,
      newSubnet,
      removeSubnet,
      removeVlan,
      routeAdded,
      routeArray,
      addRoute,
      removeRoute,
      fetchedRoutes,
      selectedVLAN,
      selectedSubnet,
      displayDropDown,
      showItems: showDropDown,
      addingItem,
      editTarget,
      selectedTarget,
      removeTarget,
      deletingItem,
      user,
      canChangeProbe, 
      canChangeVLAN,
      canChangeSubnet,
      canViewVLAN,
      canViewSubnet ,
      canDeleteVLAN, 
      canDeleteSubnet,
      canAddVLAN,
      canAddSubnet,
      fieldErrors,
      errorCode,
      errorMessage,
      clearFieldError,
      startTutorial,
      permsLoaded,
    };
  },
};
</script>
